import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, Loader2, Sparkles, Check, X, Shirt, Camera } from "lucide-react";
import { api, imgUrl, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useLocale } from "@/context/LocaleContext";
import SellerFlowSteps from "@/components/SellerFlowSteps";
import { CrosslistSubnav } from "@/components/SellerToolsNav";

const MAX_FILES = 12;
const inp = "w-full px-3 py-2 border border-aura-border outline-none text-sm rounded-lg";
const FIT_OPTIONS = ["Slim", "Regular", "Relaxed", "Oversized", "Tailored"];
const CLOSURE_OPTIONS = [
  "Zipper",
  "Button",
  "Pullover",
  "Hook-and-eye",
  "Drawstring",
  "Tie",
  "Lace-up",
  "Snap",
  "Drape / pallu",
];

function withOption(options, value) {
  const v = (value || "").trim();
  if (!v || options.includes(v)) return options;
  return [v, ...options];
}

function catalogText(cat) {
  return `${cat?.garment || ""} ${cat?.category || ""} ${cat?.title || ""}`.toLowerCase();
}

function fallbackFit(cat) {
  const text = catalogText(cat);
  if (/(skinny|slim-fit|slim fit)/.test(text)) return "Slim";
  if (/(oversized|boyfriend|boxy)/.test(text)) return "Oversized";
  if (/(tailored|suit|blazer|structured)/.test(text)) return "Tailored";
  if (/(relaxed|hoodie|lounge)/.test(text)) return "Relaxed";
  return "Regular";
}

function fallbackClosure(cat) {
  const text = catalogText(cat);
  if (/(saree|sari)/.test(text)) return "Drape / pallu";
  if (/(sneaker|boot|oxford)/.test(text)) return "Lace-up";
  if (/(hoodie|sweatshirt)/.test(text)) return "Pullover";
  if (/(jean|denim|trouser|pant)/.test(text)) return "Button";
  if (/(gown|dress|skirt|jacket|coat|lehenga)/.test(text)) return "Zipper";
  if (/(shirt|blouse|cardigan)/.test(text)) return "Button";
  return "Pullover";
}

function Labeled({ label, children }) {
  return (
    <label className="block text-[10px] tracking-luxe uppercase text-aura-muted">
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2 pt-2 border-t border-aura-border">
      <div className="text-[10px] tracking-luxe uppercase text-aura-accent">{title}</div>
      {children}
    </div>
  );
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

async function runPool(items, limit, worker) {
  let i = 0;
  const run = async () => {
    while (i < items.length) {
      const idx = i++;
      await worker(items[idx], idx);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
}

export default function AddProduct() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { categories, sizes, currencies, countries } = useLocale();
  const fileRef = useRef(null);
  const touchedRef = useRef({});
  const [hint, setHint] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [originCountry, setOriginCountry] = useState("US");
  const [shipsWorldwide, setShipsWorldwide] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [shipDomestic, setShipDomestic] = useState(8);
  const [shipInternational, setShipInternational] = useState(25);
  const [items, setItems] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    const id = params.get("template");
    if (!id) return;
    api.get("/seller/templates").then(({ data }) => {
      const t = (data.templates || []).find((x) => x.id === id);
      if (!t) return;
      setTemplate(t);
      const f = t.fields || {};
      if (f.hint) setHint(f.hint);
      if (f.currency) setCurrency(f.currency);
      if (f.origin_country) setOriginCountry(f.origin_country);
      if (f.quantity != null) setQuantity(f.quantity);
      if (f.shipping_domestic != null) setShipDomestic(f.shipping_domestic);
      if (f.shipping_international != null) setShipInternational(f.shipping_international);
    }).catch(() => {});
  }, [params]);

  const patch = (id, extra) => {
    setItems((rows) => rows.map((r) => (r.id === id ? { ...r, ...extra } : r)));
  };

  const identifyOne = async (row) => {
    patch(row.id, { status: "working", error: "" });
    const fd = new FormData();
    fd.append("file", row.file);
    if (hint) fd.append("hint", hint);
    const existing = { ...(template?.fields || {}), ...(row.catalog || {}) };
    const locked = [...(touchedRef.current[row.id] || [])];
    Object.entries(template?.fields || {}).forEach(([key, value]) => {
      if (value !== "" && value != null && !locked.includes(key)) locked.push(key);
    });
    if (Object.keys(existing).length) fd.append("existing", JSON.stringify(existing));
    if (locked.length) fd.append("locked", JSON.stringify(locked));
    try {
      const { data } = await api.post("/ai/catalog", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const cat = data.catalog || {};
      const f = template?.fields || {};
      patch(row.id, {
        status: "ready",
        catalog: {
          ...cat,
          category: cat.category || f.category || "",
          brand: cat.brand || f.brand || "",
          condition: cat.condition || f.condition || "New",
          size: cat.size || f.size || "M",
          description: cat.description || f.description || "",
          tags: (cat.tags && cat.tags.length ? cat.tags : f.tags) || [],
          fabric: cat.fabric || f.fabric || "",
          department: cat.department || f.department || "",
          fit_type: cat.fit_type || fallbackFit(cat),
          closure_type: cat.closure_type || fallbackClosure(cat),
        },
        imagePath: data.image_path,
        imageUrl: data.image_url,
        price: data.suggested_price ?? cat.price ?? "",
        compareAt: cat.compare_at_price || "",
        suggestedPrice: data.suggested_price,
        priceLow: data.price_low,
        priceHigh: data.price_high,
        comps: data.comps || [],
        selected: true,
        studioUrl: null,
        shooting: false,
        shopHero: "original",
      });
    } catch (e) {
      patch(row.id, { status: "error", error: apiError(e), selected: false });
    }
  };

  const addFiles = async (list) => {
    const incoming = Array.from(list || []).filter((f) => f.type.startsWith("image/"));
    if (!incoming.length) return;
    const room = MAX_FILES - items.length;
    if (room <= 0) return toast.error(`Max ${MAX_FILES} pieces at once`);
    const slice = incoming.slice(0, room);
    const next = slice.map((file) => ({
      id: uid(),
      file,
      preview: URL.createObjectURL(file),
      status: "queued",
      error: "",
      catalog: null,
      selected: false,
      price: "",
      comps: [],
    }));
    setItems((rows) => [...rows, ...next]);
    await runPool(next, 2, identifyOne);
  };

  const studioModelDesc = (cat) => {
    const dept = (cat?.department || "").toLowerCase();
    const who = dept === "men" || dept === "boys" ? "male model" : "female model";
    return `a regular ${who}`;
  };

  const shootStudio = async (row) => {
    if (!row.imagePath) return toast.error("Wait for the garment upload to finish");
    patch(row.id, { shooting: true });
    try {
      const { data } = await api.post(
        "/ai/photoshoot",
        {
          image_path: row.imagePath,
          background: "minimalist studio",
          model_desc: studioModelDesc(row.catalog),
          garment_hint: row.catalog?.garment || row.catalog?.title || "",
        },
        { timeout: 180000 },
      );
      patch(row.id, { studioUrl: data.image_url, shopHero: "studio", shooting: false });
      toast.success("Studio shot ready — it will be the shop image");
    } catch (e) {
      patch(row.id, { shooting: false });
      toast.error(apiError(e));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const setCat = (id, key, value) => {
    touchedRef.current[id] = touchedRef.current[id] || new Set();
    touchedRef.current[id].add(key);
    setItems((rows) =>
      rows.map((r) => (r.id === id ? { ...r, catalog: { ...r.catalog, [key]: value } } : r))
    );
  };

  const setBullet = (id, idx, value) => {
    setItems((rows) =>
      rows.map((r) => {
        if (r.id !== id) return r;
        const bullets = [...(r.catalog?.bullets || ["", "", "", "", ""])];
        while (bullets.length < 5) bullets.push("");
        bullets[idx] = value;
        return { ...r, catalog: { ...r.catalog, bullets } };
      })
    );
  };

  const setCsv = (id, key, value) => {
    setCat(id, key, value.split(",").map((s) => s.trim()).filter(Boolean));
  };

  const selected = items.filter((r) => r.selected && r.status === "ready" && Number(r.price) > 0);
  const working = items.some((r) => r.status === "queued" || r.status === "working");

  const saveSelectedDrafts = async () => {
    if (!selected.length) {
      toast.error("Select at least one priced draft");
      return [];
    }
    setPublishing(true);
    const created = [];
    try {
      for (const row of selected) {
        const c = row.catalog;
        const original = row.imageUrl;
        const studio = row.studioUrl;
        const hero = row.shopHero === "studio" && studio ? studio : original;
        const images = [hero, original, studio].filter((u, i, arr) => u && arr.indexOf(u) === i);
        const { data } = await api.post("/products", {
          title: c.title,
          description: c.description,
          price: Number(row.price),
          compare_at_price: row.compareAt ? Number(row.compareAt) : null,
          category: c.category,
          fabric: c.fabric,
          occasion: c.occasion,
          style: c.style,
          color: c.color,
          size: c.size,
          tags: c.tags || [],
          search_terms: c.search_terms || [],
          bullets: (c.bullets || []).filter(Boolean),
          images,
          brand: c.brand || "",
          manufacturer: c.manufacturer || c.brand || "",
          sku: c.sku || "",
          asin: c.asin || "",
          gtin: c.gtin || c.upc || "",
          upc: c.upc || c.gtin || "",
          ean: c.ean || "",
          fsn: c.fsn || "",
          garment: c.garment || "",
          department: c.department || "",
          condition: c.condition || "New",
          pattern: c.pattern || "",
          fit_type: c.fit_type || fallbackFit(c),
          neckline: c.neckline || "",
          sleeve_length: c.sleeve_length || "",
          garment_length: c.garment_length || "",
          closure_type: c.closure_type || fallbackClosure(c),
          care_instructions: c.care_instructions || "",
          season: c.season || "",
          material_composition: c.material_composition || "",
          country_of_origin: c.country_of_origin || originCountry,
          package_weight: c.package_weight === "" || c.package_weight == null ? null : Number(c.package_weight),
          package_weight_unit: c.package_weight_unit || "",
          battery_quantity: c.battery_quantity === "" || c.battery_quantity == null ? null : Number(c.battery_quantity),
          hsn: c.hsn || "",
          model_name: c.model_name || "",
          part_number: c.part_number || "",
          number_of_items: c.number_of_items === "" || c.number_of_items == null ? null : Number(c.number_of_items),
          weave_type: c.weave_type || "",
          weave_method: c.weave_method || "",
          embroidery_type: c.embroidery_type || "",
          included_components: c.included_components || "",
          embellishment: c.embellishment || "",
          manufacturer_contact: c.manufacturer_contact || "",
          importer_contact: c.importer_contact || "",
          packer_contact: c.packer_contact || "",
          item_length: c.item_length === "" || c.item_length == null ? null : Number(c.item_length),
          item_length_unit: c.item_length_unit || "",
          item_weight: c.item_weight === "" || c.item_weight == null ? null : Number(c.item_weight),
          item_weight_unit: c.item_weight_unit || "",
          package_length: c.package_length === "" || c.package_length == null ? null : Number(c.package_length),
          package_width: c.package_width === "" || c.package_width == null ? null : Number(c.package_width),
          package_height: c.package_height === "" || c.package_height == null ? null : Number(c.package_height),
          blouse_included: c.blouse_included || "",
          blouse_material: c.blouse_material || "",
          blouse_length: c.blouse_length === "" || c.blouse_length == null ? null : Number(c.blouse_length),
          blouse_length_unit: c.blouse_length_unit || "",
          saree_design: c.saree_design || "",
          holiday_type: c.holiday_type || "",
          life_event: c.life_event || "",
          special_size: c.special_size || "",
          is_customizable: c.is_customizable || "",
          color_map: c.color_map || "",
          is_live: false,
          currency,
          origin_country: originCountry,
          ships_to: shipsWorldwide ? ["*"] : [originCountry],
          quantity: Math.max(0, Number(quantity) || 1),
          shipping_domestic: Math.max(0, Number(shipDomestic) || 0),
          shipping_international: Math.max(0, Number(shipInternational) || 0),
        });
        created.push(data);
        patch(row.id, { status: "drafted", selected: false, productId: data.id });
      }
      return created;
    } catch (e) {
      toast.error(created.length ? `Saved ${created.length}, then failed: ${apiError(e)}` : apiError(e));
      return created;
    } finally {
      setPublishing(false);
    }
  };

  const continueToStudio = async () => {
    const created = await saveSelectedDrafts();
    if (!created.length) return;
    toast.success(created.length === 1 ? "Draft saved — next, studio shot" : `${created.length} drafts saved — next, studio shot`);
    navigate(`/seller/photoshoot?listing=${created[0].id}`);
  };

  const saveDraftsOnly = async () => {
    const created = await saveSelectedDrafts();
    if (!created.length) return;
    toast.success(created.length === 1 ? "Draft saved. Open studio when you want a model shot, then publish." : `${created.length} drafts saved. They stay off the shop until you publish.`);
    navigate("/seller/products");
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <CrosslistSubnav />
      <div className="flex items-center gap-2 text-[11px] tracking-luxe uppercase text-aura-accent mb-2">
        <Sparkles className="w-3.5 h-3.5" /> Closet ingest
      </div>
      <h1 className="text-3xl font-semibold mb-2">Create a listing</h1>
      <p className="text-sm text-aura-muted mb-6 max-w-2xl">
        Ingest the garment, generate a studio shot, then publish when the listing is ready. Drafts stay off the shop until you say so.
        {template ? ` Using template “${template.name}”.` : ""}
      </p>
      <SellerFlowSteps current={1} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Note for AI (optional)</label>
          <input
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="e.g. vintage 90s, all from one atelier"
            data-testid="closet-hint"
            className="mt-2 w-full px-3 py-2.5 border border-aura-border focus:border-aura-accent outline-none text-sm rounded-lg"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Currency</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm rounded-lg bg-aura-surface"
          >
            {(currencies || []).map((c) => (
              <option key={c.code} value={c.code}>{c.code} · {c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Ships from</label>
          <select
            value={originCountry}
            onChange={(e) => setOriginCountry(e.target.value)}
            className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm rounded-lg bg-aura-surface"
          >
            {(countries || []).map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        data-testid="closet-dropzone"
        className="w-full border-2 border-dashed border-aura-border hover:border-aura-accent transition-colors rounded-lg py-12 flex flex-col items-center gap-3 bg-aura-surface mb-8"
      >
        <Upload className="w-6 h-6 text-aura-muted" />
        <span className="text-sm">Drop a rack of photos, or click to upload</span>
        <span className="text-xs text-aura-muted">Up to {MAX_FILES} images · no price needed up front</span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid="closet-file-input"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {items.length > 0 && (
        <div className="space-y-4 mb-8" data-testid="closet-drafts">
          {items.map((row) => (
            <div
              key={row.id}
              data-testid={`closet-card-${row.id}`}
              className={`border rounded-lg bg-aura-surface overflow-hidden ${
                row.selected ? "border-aura-accent" : "border-aura-border"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-0">
                <div className="aspect-[3/4] md:aspect-auto md:min-h-[180px] bg-aura-sand relative">
                  <img src={row.shopHero === "studio" && row.studioUrl ? imgUrl(row.studioUrl) : row.preview} alt="" className="w-full h-full object-cover" />
                  {row.status === "ready" && (
                    <div className="absolute bottom-2 left-2 right-2 space-y-1.5">
                      <button
                        type="button"
                        onClick={() => shootStudio(row)}
                        disabled={row.shooting}
                        data-testid={`closet-studio-${row.id}`}
                        className="w-full py-1.5 px-2 bg-black/70 text-white text-[10px] tracking-luxe uppercase rounded flex items-center justify-center gap-1.5 disabled:opacity-50"
                      >
                        {row.shooting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                        {row.shooting ? "Studio shot…" : row.studioUrl ? "Regenerate studio shot" : "Studio shot for shop"}
                      </button>
                      {row.studioUrl && (
                        <div className="flex gap-1">
                          <button type="button" onClick={() => patch(row.id, { shopHero: "studio" })} className={`flex-1 py-1 text-[10px] rounded ${row.shopHero === "studio" ? "bg-aura-accent text-white" : "bg-black/70 text-white"}`}>Studio</button>
                          <button type="button" onClick={() => patch(row.id, { shopHero: "original" })} className={`flex-1 py-1 text-[10px] rounded ${row.shopHero === "original" ? "bg-aura-accent text-white" : "bg-black/70 text-white"}`}>Original</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs text-aura-muted">
                      {row.status === "working" || row.status === "queued" ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Identifying garment and brand…</>
                      ) : row.status === "error" ? (
                        <span className="text-red-400">{row.error}</span>
                      ) : row.status === "drafted" ? (
                        <span className="text-aura-accent">Saved as draft</span>
                      ) : row.status === "published" ? (
                        <span className="text-aura-accent">Published</span>
                      ) : (
                        <span className="uppercase tracking-luxe text-aura-accent">
                          {row.catalog?.brand || "Independent"} · {row.catalog?.garment || row.catalog?.category}
                        </span>
                      )}
                    </div>
                    {row.status === "ready" && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={(e) => patch(row.id, { selected: e.target.checked })}
                          data-testid={`closet-select-${row.id}`}
                          className="w-4 h-4 accent-aura-accent"
                        />
                        Include
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => setItems((rows) => rows.filter((r) => r.id !== row.id))}
                      className="text-aura-muted hover:text-white"
                      aria-label="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {row.status === "ready" && row.catalog && (
                    <>
                      <Section title="1 · Product identity">
                        <input
                          value={row.catalog.title || ""}
                          onChange={(e) => setCat(row.id, "title", e.target.value)}
                          placeholder="Product name"
                          className="w-full px-3 py-2 border border-aura-border focus:border-aura-accent outline-none text-sm rounded-lg"
                        />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <Labeled label="Brand">
                            <input value={row.catalog.brand || ""} onChange={(e) => setCat(row.id, "brand", e.target.value)} className={inp} />
                          </Labeled>
                          <Labeled label="Manufacturer">
                            <input value={row.catalog.manufacturer || ""} onChange={(e) => setCat(row.id, "manufacturer", e.target.value)} className={inp} />
                          </Labeled>
                          <Labeled label="SKU">
                            <input value={row.catalog.sku || ""} onChange={(e) => setCat(row.id, "sku", e.target.value)} className={`${inp} font-mono`} />
                          </Labeled>
                          <Labeled label="ASIN">
                            <input value={row.catalog.asin || ""} onChange={(e) => setCat(row.id, "asin", e.target.value)} placeholder="Amazon catalog" className={`${inp} font-mono`} />
                          </Labeled>
                          <Labeled label="UPC / GTIN">
                            <input value={row.catalog.upc || row.catalog.gtin || ""} onChange={(e) => setCat(row.id, "upc", e.target.value)} placeholder="Shopify / Amazon new listing" className={`${inp} font-mono`} />
                          </Labeled>
                          <Labeled label="FSN">
                            <input value={row.catalog.fsn || ""} onChange={(e) => setCat(row.id, "fsn", e.target.value)} placeholder="Flipkart product ID" className={`${inp} font-mono`} />
                          </Labeled>
                          <Labeled label="Product type">
                            <input value={row.catalog.garment || ""} onChange={(e) => setCat(row.id, "garment", e.target.value)} className={inp} />
                          </Labeled>
                          <Labeled label="Browse category">
                            <select value={row.catalog.category || categories?.[0] || "Electronics"} onChange={(e) => setCat(row.id, "category", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {(categories || []).map((c) => <option key={c}>{c}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="Department">
                            <select value={row.catalog.department || "Unisex"} onChange={(e) => setCat(row.id, "department", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {["Women", "Men", "Unisex", "Girls", "Boys"].map((d) => <option key={d}>{d}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="Condition">
                            <select value={row.catalog.condition || "New"} onChange={(e) => setCat(row.id, "condition", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {["New", "Like new", "Good", "Vintage"].map((d) => <option key={d}>{d}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="Country of origin">
                            <input value={row.catalog.country_of_origin || ""} onChange={(e) => setCat(row.id, "country_of_origin", e.target.value)} placeholder="e.g. IN" className={inp} />
                          </Labeled>
                        </div>
                      </Section>

                      <Section title="2 · Key product features">
                        {(row.catalog.bullets || ["", "", "", "", ""]).slice(0, 5).map((b, i) => (
                          <input
                            key={i}
                            value={b}
                            onChange={(e) => setBullet(row.id, i, e.target.value)}
                            placeholder={`Bullet ${i + 1}`}
                            className={inp}
                          />
                        ))}
                      </Section>

                      <Section title="3 · Product description">
                        <textarea
                          value={row.catalog.description || ""}
                          onChange={(e) => setCat(row.id, "description", e.target.value)}
                          rows={5}
                          className={`${inp} resize-y min-h-[96px]`}
                        />
                        <Labeled label="Search terms (comma-separated)">
                          <input
                            value={(row.catalog.search_terms || []).join(", ")}
                            onChange={(e) => setCsv(row.id, "search_terms", e.target.value)}
                            placeholder="saree, silk, wedding, banarasi"
                            className={inp}
                          />
                        </Labeled>
                        <Labeled label="Generic keywords / tags">
                          <input
                            value={(row.catalog.tags || []).join(", ")}
                            onChange={(e) => setCsv(row.id, "tags", e.target.value)}
                            className={inp}
                          />
                        </Labeled>
                      </Section>

                      <Section title="4 · Product details">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <Labeled label="Color"><input value={row.catalog.color || ""} onChange={(e) => setCat(row.id, "color", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Size">
                            <select value={row.catalog.size || "M"} onChange={(e) => setCat(row.id, "size", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {(sizes || []).map((s) => <option key={s}>{s}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="Fabric"><input value={row.catalog.fabric || ""} onChange={(e) => setCat(row.id, "fabric", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Material composition"><input value={row.catalog.material_composition || ""} onChange={(e) => setCat(row.id, "material_composition", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Pattern"><input value={row.catalog.pattern || ""} onChange={(e) => setCat(row.id, "pattern", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Fit">
                            <select value={row.catalog.fit_type || fallbackFit(row.catalog)} onChange={(e) => setCat(row.id, "fit_type", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {withOption(FIT_OPTIONS, row.catalog.fit_type || fallbackFit(row.catalog)).map((o) => <option key={o}>{o}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="Neckline"><input value={row.catalog.neckline || ""} onChange={(e) => setCat(row.id, "neckline", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Sleeve"><input value={row.catalog.sleeve_length || ""} onChange={(e) => setCat(row.id, "sleeve_length", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Length"><input value={row.catalog.garment_length || ""} onChange={(e) => setCat(row.id, "garment_length", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Closure">
                            <select value={row.catalog.closure_type || fallbackClosure(row.catalog)} onChange={(e) => setCat(row.id, "closure_type", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {withOption(CLOSURE_OPTIONS, row.catalog.closure_type || fallbackClosure(row.catalog)).map((o) => <option key={o}>{o}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="Occasion"><input value={row.catalog.occasion || ""} onChange={(e) => setCat(row.id, "occasion", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Style"><input value={row.catalog.style || ""} onChange={(e) => setCat(row.id, "style", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Season"><input value={row.catalog.season || ""} onChange={(e) => setCat(row.id, "season", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Care"><input value={row.catalog.care_instructions || ""} onChange={(e) => setCat(row.id, "care_instructions", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Package weight">
                            <input type="number" min="0" value={row.catalog.package_weight ?? ""} onChange={(e) => setCat(row.id, "package_weight", e.target.value)} placeholder="500" className={`${inp} font-mono`} />
                          </Labeled>
                          <Labeled label="Weight unit">
                            <select value={row.catalog.package_weight_unit || "grams"} onChange={(e) => setCat(row.id, "package_weight_unit", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {["grams", "kilograms", "pounds", "ounces"].map((unit) => <option key={unit}>{unit}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="HSN code">
                            <input value={row.catalog.hsn || ""} onChange={(e) => setCat(row.id, "hsn", e.target.value)} placeholder="4–8 digits" className={`${inp} font-mono`} />
                          </Labeled>
                          <Labeled label="Number of batteries">
                            <input type="number" min="0" value={row.catalog.battery_quantity ?? ""} onChange={(e) => setCat(row.id, "battery_quantity", e.target.value)} placeholder="0" className={`${inp} font-mono`} />
                          </Labeled>
                        </div>
                      </Section>

                      <Section title="5 · Offer">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <Labeled label={`Your price (${currency})`}>
                            <input type="number" min="1" step="0.01" value={row.price} onChange={(e) => patch(row.id, { price: e.target.value })} data-testid={`closet-price-${row.id}`} className={`${inp} font-mono`} />
                          </Labeled>
                          <Labeled label="List price / MRP (optional)">
                            <input type="number" min="0" step="0.01" value={row.compareAt || ""} onChange={(e) => patch(row.id, { compareAt: e.target.value })} className={`${inp} font-mono`} />
                          </Labeled>
                        </div>
                        {(row.suggestedPrice != null || row.comps?.length > 0) && (
                          <div className="text-xs text-aura-muted">
                            Suggested {currency} {row.suggestedPrice ?? "—"}
                            {row.priceLow != null && row.priceHigh != null ? ` · comps ${row.priceLow}–${row.priceHigh}` : ""}
                            {row.comps?.length ? (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {row.comps.slice(0, 4).map((c, i) => (
                                  <span key={i} className="px-2 py-1 rounded-full bg-aura-bg border border-aura-border">
                                    {["channel3", "rakuten", "cj", "awin", "flexoffers", "amazon_associates", "ebay", "walmart_affiliate", "skimlinks", "target", "bestbuy", "admitad", "flipkart_affiliate", "quickcommerce", "partner"].includes(c.source) ? "Partner" : "Shoptosa"} · {c.price}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span> · no close comps yet — edit the price if needed</span>
                            )}
                          </div>
                        )}
                      </Section>

                      <Section title="6 · Amazon details">
                        <p className="text-[11px] text-aura-muted">These match Seller Central’s recommended fields. Dangerous goods is sent as not applicable. Leave a box blank when it does not apply. Separate materials, occasions, and components with commas. Saree design is limited to 50 characters.</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <Labeled label="Model name"><input value={row.catalog.model_name || ""} onChange={(e) => setCat(row.id, "model_name", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Part number"><input value={row.catalog.part_number || ""} onChange={(e) => setCat(row.id, "part_number", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Number of items"><input type="number" min="1" value={row.catalog.number_of_items ?? ""} onChange={(e) => setCat(row.id, "number_of_items", e.target.value)} placeholder="1" className={`${inp} font-mono`} /></Labeled>
                          <Labeled label="Special size"><input value={row.catalog.special_size || ""} onChange={(e) => setCat(row.id, "special_size", e.target.value)} placeholder="Regular" className={inp} /></Labeled>
                          <Labeled label="Color map"><input value={row.catalog.color_map || ""} onChange={(e) => setCat(row.id, "color_map", e.target.value)} placeholder="Same as color" className={inp} /></Labeled>
                          <Labeled label="Weave type"><input value={row.catalog.weave_type || ""} onChange={(e) => setCat(row.id, "weave_type", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Weave method"><input value={row.catalog.weave_method || ""} onChange={(e) => setCat(row.id, "weave_method", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Embroidery type"><input value={row.catalog.embroidery_type || ""} onChange={(e) => setCat(row.id, "embroidery_type", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Included components"><input value={row.catalog.included_components || ""} onChange={(e) => setCat(row.id, "included_components", e.target.value)} placeholder="Saree, blouse piece" className={inp} /></Labeled>
                          <Labeled label="Embellishment"><input value={row.catalog.embellishment || ""} onChange={(e) => setCat(row.id, "embellishment", e.target.value)} placeholder="Zari work, floral motifs" className={inp} /></Labeled>
                          <Labeled label="Holiday"><input value={row.catalog.holiday_type || ""} onChange={(e) => setCat(row.id, "holiday_type", e.target.value)} placeholder="Diwali, Navratri" className={inp} /></Labeled>
                          <Labeled label="Life event"><input value={row.catalog.life_event || ""} onChange={(e) => setCat(row.id, "life_event", e.target.value)} placeholder="Wedding" className={inp} /></Labeled>
                          <Labeled label="Customisable">
                            <select value={row.catalog.is_customizable || ""} onChange={(e) => setCat(row.id, "is_customizable", e.target.value)} className={`${inp} bg-aura-surface`}>
                              <option value=""> </option>
                              <option>Yes</option>
                              <option>No</option>
                            </select>
                          </Labeled>
                          <Labeled label="Saree design"><input value={row.catalog.saree_design || ""} maxLength={50} onChange={(e) => setCat(row.id, "saree_design", e.target.value)} placeholder="50 characters" className={inp} /></Labeled>
                          <Labeled label="Item length"><input type="number" min="0" step="0.01" value={row.catalog.item_length ?? ""} onChange={(e) => setCat(row.id, "item_length", e.target.value)} className={`${inp} font-mono`} /></Labeled>
                          <Labeled label="Length unit">
                            <select value={row.catalog.item_length_unit || "centimeters"} onChange={(e) => setCat(row.id, "item_length_unit", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {["centimeters", "meters", "inches"].map((unit) => <option key={unit}>{unit}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="Item weight"><input type="number" min="0" step="0.01" value={row.catalog.item_weight ?? ""} onChange={(e) => setCat(row.id, "item_weight", e.target.value)} className={`${inp} font-mono`} /></Labeled>
                          <Labeled label="Item weight unit">
                            <select value={row.catalog.item_weight_unit || "kilograms"} onChange={(e) => setCat(row.id, "item_weight_unit", e.target.value)} className={`${inp} bg-aura-surface`}>
                              {["grams", "kilograms", "pounds"].map((unit) => <option key={unit}>{unit}</option>)}
                            </select>
                          </Labeled>
                          <Labeled label="Package length"><input type="number" min="0" step="0.01" value={row.catalog.package_length ?? ""} onChange={(e) => setCat(row.id, "package_length", e.target.value)} className={`${inp} font-mono`} /></Labeled>
                          <Labeled label="Package width"><input type="number" min="0" step="0.01" value={row.catalog.package_width ?? ""} onChange={(e) => setCat(row.id, "package_width", e.target.value)} className={`${inp} font-mono`} /></Labeled>
                          <Labeled label="Package height"><input type="number" min="0" step="0.01" value={row.catalog.package_height ?? ""} onChange={(e) => setCat(row.id, "package_height", e.target.value)} className={`${inp} font-mono`} /></Labeled>
                          <Labeled label="Blouse included">
                            <select value={row.catalog.blouse_included || ""} onChange={(e) => setCat(row.id, "blouse_included", e.target.value)} className={`${inp} bg-aura-surface`}>
                              <option value=""> </option>
                              <option>Yes</option>
                              <option>No</option>
                            </select>
                          </Labeled>
                          <Labeled label="Blouse material"><input value={row.catalog.blouse_material || ""} onChange={(e) => setCat(row.id, "blouse_material", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Blouse length"><input type="number" min="0" step="0.01" value={row.catalog.blouse_length ?? ""} onChange={(e) => setCat(row.id, "blouse_length", e.target.value)} className={`${inp} font-mono`} /></Labeled>
                          <Labeled label="Manufacturer contact"><input value={row.catalog.manufacturer_contact || ""} onChange={(e) => setCat(row.id, "manufacturer_contact", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Packer contact"><input value={row.catalog.packer_contact || ""} onChange={(e) => setCat(row.id, "packer_contact", e.target.value)} className={inp} /></Labeled>
                          <Labeled label="Importer contact"><input value={row.catalog.importer_contact || ""} onChange={(e) => setCat(row.id, "importer_contact", e.target.value)} placeholder="N/A if you make it" className={inp} /></Labeled>
                        </div>
                      </Section>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
          Qty each
          <input type="number" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="closet-qty" className="mt-1 w-20 px-2 py-2 border border-aura-border outline-none text-sm font-mono rounded-lg bg-aura-surface" />
        </label>
        <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
          Domestic shipping fee
          <input type="number" min="0" step="0.01" value={shipDomestic} onChange={(e) => setShipDomestic(e.target.value)} data-testid="closet-ship-dom" className="mt-1 w-28 px-2 py-2 border border-aura-border outline-none text-sm font-mono rounded-lg bg-aura-surface" />
        </label>
        <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
          International shipping fee
          <input type="number" min="0" step="0.01" value={shipInternational} onChange={(e) => setShipInternational(e.target.value)} data-testid="closet-ship-intl" className="mt-1 w-28 px-2 py-2 border border-aura-border outline-none text-sm font-mono rounded-lg bg-aura-surface" />
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
          <input type="checkbox" checked={shipsWorldwide} onChange={(e) => setShipsWorldwide(e.target.checked)} className="w-4 h-4 accent-aura-accent" />
          Ships worldwide
        </label>
        <button
          type="button"
          onClick={saveDraftsOnly}
          disabled={publishing || working || !selected.length}
          data-testid="closet-save-drafts"
          className="ml-auto px-5 py-3 border border-aura-border text-sm font-medium flex items-center gap-2 rounded-lg disabled:opacity-50"
        >
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shirt className="w-4 h-4" />}
          Save drafts
        </button>
        <button
          type="button"
          onClick={continueToStudio}
          disabled={publishing || working || !selected.length}
          data-testid="closet-continue-studio"
          className="px-6 py-3 btn-accent text-sm font-medium flex items-center gap-2 rounded-lg disabled:opacity-50"
        >
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : selected.length ? <Camera className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {publishing ? "Saving…" : selected.length ? `Save ${selected.length} → AI studio` : "Save → AI studio"}
        </button>
      </div>
    </div>
  );
}
